import type { Bar } from '../core/types'

// Deterministic synthetic OHLC generator (seeded random walk).
// Used for local dev and unit tests before real data prep exists.

function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export interface SynthOpts {
  count?: number
  startTime?: number // UTC epoch seconds; default 2020-01-01
  stepSeconds?: number // default 3600 (h1)
  startPrice?: number
  volatility?: number // fraction of price, per bar
  seed?: number
}

export function genSyntheticBars(opts: SynthOpts = {}): Bar[] {
  const count = opts.count ?? 2000
  const step = opts.stepSeconds ?? 3600
  const start = opts.startTime ?? Date.UTC(2020, 0, 1) / 1000
  const vol = opts.volatility ?? 0.004
  const rnd = mulberry32(opts.seed ?? 42)

  const bars: Bar[] = []
  let price = opts.startPrice ?? 2000
  for (let i = 0; i < count; i++) {
    const time = start + i * step
    const drift = (rnd() - 0.5) * 2 * vol * price
    const open = price
    const close = Math.max(0.01, open + drift)
    const wick = vol * price * rnd()
    const high = Math.max(open, close) + wick
    const low = Math.min(open, close) - wick
    const volume = Math.round(100 + rnd() * 900)
    bars.push({
      time,
      open: round2(open),
      high: round2(high),
      low: round2(Math.max(0.01, low)),
      close: round2(close),
      volume,
    })
    price = close
  }
  return bars
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}
